import { useState } from "react";
import { Bell, BellRing, Check, ChevronRight } from "lucide-react";
import {
  Body,
  Card,
  CardTitle,
  Caption,
  cn,
  VBadge,
  VButton,
  VDialog,
  VDialogContent,
  VField,
  VInput,
  VSelect,
} from "@/components/ui";
import type { V2Task } from "@/v2/shared/model";
import type { TaskTarget } from "@/v2/data/db";
import {
  daysUntil,
  formatDate,
  formatDateTime,
  fullName,
} from "@/v2/shared/format";
import { V2_USERS } from "@/v2/auth/users";
import { useCurrentUser } from "@/v2/auth/useCurrentUser";
import { useSetTaskReminder, useToggleTask } from "@/v2/data/mutations";

// CB-17: één vaste, afvinkbare takenlijst (aanvraag, dossier of inzet). De
// lijsten zijn hardcoded (niet beheerbaar) en worden automatisch aangemaakt;
// afvinken gebeurt met auteur en tijdstip en komt in de historiek. Op een open
// taak kan optioneel een herinnering (datum, eventueel tijdstip) gezet worden;
// vervallen herinneringen verschijnen als melding voor alle beheerders.
// Zodra alle taken afgewerkt zijn klapt de kaart in tot enkel de header; een
// klik op de header klapt ze weer open.
export function TaskGroup({
  requestId,
  title,
  target,
  tasks,
  canToggle,
}: {
  requestId: string;
  title: string;
  target: TaskTarget;
  tasks: ReadonlyArray<V2Task>;
  canToggle: boolean;
}): React.JSX.Element {
  const user = useCurrentUser();
  const author = fullName(user.firstName, user.lastName);
  const toggle = useToggleTask();
  const doneCount = tasks.filter((t) => t.done).length;
  const [reminderTask, setReminderTask] = useState<V2Task | null>(null);
  const allDone = tasks.length > 0 && doneCount === tasks.length;
  const [openWhenDone, setOpenWhenDone] = useState(false);
  // Opnieuw inklappen zodra een heropende taak terug afgevinkt wordt.
  if (!allDone && openWhenDone) setOpenWhenDone(false);
  const collapsed = allDone && !openWhenDone;
  const lastDone = allDone
    ? tasks.reduce((a, b) => ((b.doneAt ?? "") > (a.doneAt ?? "") ? b : a))
    : undefined;

  return (
    <Card padding="none">
      {allDone ? (
        <VButton
          variant="ghost"
          fullWidth
          aria-expanded={!collapsed}
          onClick={() => setOpenWhenDone((o) => !o)}
          className={cn(
            "h-auto justify-start gap-3 px-5 py-3 text-left font-normal hover:bg-muted/50 focus-visible:ring-inset focus-visible:ring-offset-0",
            collapsed ? "rounded-xl" : "rounded-b-none rounded-t-xl border-b border-border",
          )}
        >
          <ChevronRight
            className={cn(
              "size-4 shrink-0 text-muted-foreground transition-transform",
              !collapsed && "rotate-90",
            )}
            aria-hidden
          />
          <span className="min-w-0 flex-1">
            <Body as="span" weight="semibold" className="block truncate">
              {title}
            </Body>
            {lastDone?.doneBy ? (
              <Caption tone="muted" as="span" className="block truncate">
                Laatst afgevinkt door {lastDone.doneBy} ·{" "}
                {formatDateTime(lastDone.doneAt)}
              </Caption>
            ) : null}
          </span>
          <VBadge color="success" className="shrink-0">
            <Check className="size-3" aria-hidden />
            Afgewerkt
          </VBadge>
          <Caption tone="muted" className="shrink-0">
            {doneCount} van {tasks.length} afgewerkt
          </Caption>
        </VButton>
      ) : (
        <div className="flex items-center justify-between gap-3 border-b border-border px-5 py-3">
          <CardTitle className="text-sm">{title}</CardTitle>
          <Caption tone="muted">
            {doneCount} van {tasks.length} afgewerkt
          </Caption>
        </div>
      )}
      {collapsed ? null : (
        <ul className="divide-y divide-border">
          {tasks.map((task) => (
            <li key={task.id} className="flex items-start gap-3 px-5 py-3">
              <input
                id={`task-${task.id}`}
                type="checkbox"
                className="mt-0.5 size-4 accent-primary-600 disabled:opacity-50"
                checked={task.done}
                disabled={!canToggle || toggle.isPending}
                onChange={() =>
                  toggle.mutate({ requestId, target, taskId: task.id, author })
                }
              />
              <label
                htmlFor={`task-${task.id}`}
                className="flex-1 cursor-pointer"
              >
                <span
                  className={
                    task.done
                      ? "block text-sm text-muted-foreground line-through"
                      : "block text-sm text-foreground"
                  }
                >
                  {task.label}
                </span>
                {task.done && task.doneBy ? (
                  <Caption tone="muted">
                    Afgevinkt door {task.doneBy} · {formatDateTime(task.doneAt)}
                  </Caption>
                ) : null}
              </label>
              {!task.done && canToggle ? (
                <span className="flex shrink-0 items-center gap-2">
                  {task.reminderDate ? <ReminderChip task={task} /> : null}
                  <VButton
                    variant="ghost"
                    size="iconSm"
                    aria-label={
                      task.reminderDate
                        ? `Herinnering aanpassen voor ${task.label}`
                        : `Herinnering instellen voor ${task.label}`
                    }
                    className="text-muted-foreground hover:text-foreground"
                    onClick={() => setReminderTask(task)}
                  >
                    <Bell className="size-4" aria-hidden />
                  </VButton>
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      <ReminderDialog
        requestId={requestId}
        target={target}
        task={reminderTask}
        author={author}
        onClose={() => setReminderTask(null)}
      />
    </Card>
  );
}

// Vervallen herinneringen (vandaag of eerder) krijgen het waarschuwingsaccent;
// toekomstige blijven neutraal.
function ReminderChip({ task }: { task: V2Task }): React.JSX.Element {
  const due =
    task.reminderDate !== undefined && daysUntil(task.reminderDate) <= 0;
  return (
    <VBadge color={due ? "warning" : "neutral"} className="gap-1">
      <BellRing className="size-3" aria-hidden />
      {formatDate(task.reminderDate)}
      {task.reminderTime ? ` ${task.reminderTime}` : ""}
    </VBadge>
  );
}

function ReminderDialog({
  requestId,
  target,
  task,
  author,
  onClose,
}: {
  requestId: string;
  target: TaskTarget;
  task: V2Task | null;
  author: string;
  onClose: () => void;
}): React.JSX.Element {
  const setReminder = useSetTaskReminder();
  const managers = V2_USERS.filter((u) => u.role === "manager");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [assigneeId, setAssigneeId] = useState("");
  const [error, setError] = useState("");
  // Prefill bij openen: state volgt de geselecteerde taak.
  const [prefillFor, setPrefillFor] = useState<string | null>(null);
  if (task && prefillFor !== task.id) {
    setPrefillFor(task.id);
    setDate(task.reminderDate ?? "");
    setTime(task.reminderTime ?? "");
    setAssigneeId(task.reminderAssigneeId ?? "");
    setError("");
  }
  if (!task && prefillFor !== null) setPrefillFor(null);

  function close(): void {
    onClose();
  }

  function save(reminderDate?: string, reminderTime?: string): void {
    setReminder.mutate(
      {
        requestId,
        target,
        taskId: task?.id ?? "",
        reminderDate,
        reminderTime,
        reminderAssigneeId: reminderDate && assigneeId ? assigneeId : undefined,
        author,
      },
      { onSuccess: close },
    );
  }

  return (
    <VDialog open={task !== null} onOpenChange={(o) => (!o ? close() : null)}>
      <VDialogContent
        title="Herinnering instellen"
        description={
          task
            ? `Voor de taak "${task.label}". Zodra de datum bereikt is, verschijnt een melding in de notificatiebel: bij de gekozen beheerder, of bij het hele team als je niemand kiest.`
            : ""
        }
        size="sm"
      >
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <VField
              label="Datum"
              htmlFor="reminder-date"
              required
              error={error}
            >
              <VInput
                id="reminder-date"
                type="date"
                value={date}
                onChange={(e) => {
                  setDate(e.target.value);
                  setError("");
                }}
              />
            </VField>
            <VField label="Tijdstip" htmlFor="reminder-time" optional>
              <VInput
                id="reminder-time"
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
              />
            </VField>
          </div>
          <VField
            label="Melding gericht aan"
            htmlFor="reminder-assignee"
            optional
          >
            <VSelect
              id="reminder-assignee"
              value={assigneeId}
              onChange={(e) => setAssigneeId(e.target.value)}
              className="w-full"
            >
              <option value="">Hele team</option>
              {managers.map((m) => (
                <option key={m.id} value={m.id}>
                  {fullName(m.firstName, m.lastName)}
                </option>
              ))}
            </VSelect>
          </VField>
          <div className="flex items-center justify-between gap-2 border-t border-border pt-4">
            {task?.reminderDate ? (
              <VButton
                variant="ghost"
                className="text-error-600 hover:text-error-700"
                disabled={setReminder.isPending}
                onClick={() => save(undefined, undefined)}
              >
                Verwijderen
              </VButton>
            ) : (
              <span />
            )}
            <span className="flex items-center gap-2">
              <VButton variant="ghost" onClick={close}>
                Annuleren
              </VButton>
              <VButton
                disabled={setReminder.isPending}
                onClick={() => {
                  if (!date) {
                    setError("Kies een datum.");
                    return;
                  }
                  save(date, time || undefined);
                }}
              >
                Instellen
              </VButton>
            </span>
          </div>
        </div>
      </VDialogContent>
    </VDialog>
  );
}
